import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useCoachProfile } from "@/hooks/useCoachProfile";
import { useUnbilledSessions } from "@/hooks/useUnbilledSessions";
import { fmtMoney } from "@/lib/invoicing";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { WeatherIcon } from "@/components/calendar/WeatherIcon";
import { DashboardAlerts } from "@/components/dashboard/DashboardAlerts";
import { getForecastsForCoach, getDefaultLocationForecast } from "@/lib/weather.functions";
import { getUpcomingCalendarEvents, type UpcomingEvent } from "@/lib/google-calendar.functions";
import {
  ArrowRight,
  Rocket,
  X,
  Zap,
  Sparkles,
  Receipt,
  Mail,
  Loader2,
} from "lucide-react";

import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

export const Route = createFileRoute("/app/")({
  head: () => ({
    meta: buildAppMeta({
      title: "Dashboard — CoachQuick",
      description: "Your coaching dashboard: upcoming sessions, recent activity, unbilled lessons, and weather at a glance.",
      path: "/app",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <Dashboard />
    </RequireAuth>
  ),
});

function partOfDay(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}

function firstName(full: string | null | undefined, email: string | null | undefined) {
  const name = (full ?? "").trim().split(/\s+/)[0];
  if (name) return name;
  return (email ?? "").split("@")[0] || "there";
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function fmtTime(d: Date) {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function Dashboard() {
  const { user, session } = useAuth();
  const userId = user?.id;
  const onboarding = useOnboarding();
  const { data: profile } = useCoachProfile();
  const displayName =
    profile?.full_name ??
    (user?.user_metadata?.full_name as string | undefined) ??
    null;
  const greetingName = firstName(displayName, user?.email);

  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const eyebrow = today
    .toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    .toUpperCase()
    .replace(",", " ·");

  const statsQuery = useQuery({
    queryKey: ["coach-stats", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_coach_stats" as never, { _coach_id: userId } as never);
      if (error) throw error;
      const row = (data as any)?.[0] ?? { clients: 0, drills: 0, drafts: 0, sent: 0 };
      return {
        clients: Number(row.clients ?? 0),
        drills: Number(row.drills ?? 0),
        drafts: Number(row.drafts ?? 0),
        sent: Number(row.sent ?? 0),
      };
    },
  });

  // Safety net: a coach who lands on /app before ever seeing the wizard
  // (email verification link, bookmark, OAuth callback) gets sent to it once.
  const nav = useNavigate();
  const redirectedToOnboarding = useRef(false);
  const ob = onboarding.state;
  const obStats = statsQuery.data;
  useEffect(() => {
    if (redirectedToOnboarding.current) return;
    if (onboarding.isLoading || !obStats) return;
    const untouched =
      !ob ||
      (!ob.completed_at &&
        !ob.skipped &&
        !ob.step1_clients_done &&
        !ob.step2_library_done &&
        !ob.step3_log_done &&
        !ob.step4_calendar_done &&
        !ob.step_profile_done);
    const brandNew = obStats.clients === 0 && obStats.drills === 0 && obStats.sent === 0;
    if (untouched && brandNew) {
      redirectedToOnboarding.current = true;
      nav({ to: "/app/onboarding" });
    }
  }, [onboarding.isLoading, ob, obStats, nav]);



  const recentQuery = useQuery({
    queryKey: ["recent-sessions", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("sessions")
        .select("id, session_date, status, coach_students(student_name)")
        .eq("coach_id", userId!)
        .order("session_date", { ascending: false })
        .limit(20);
      return (data ?? []).map((r: any) => ({
        id: r.id as string,
        date: r.session_date as string,
        status: r.status as string,
        name: (r.coach_students?.student_name as string) ?? "Client",
      }));
    },
  });

  const nudgesQuery = useQuery({
    queryKey: ["pending-nudges", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("reengagement_nudges" as never)
        .select("id, student_name, days_since_last_session")
        .eq("coach_id", userId!)
        .eq("status", "pending")
        .order("days_since_last_session", { ascending: false })
        .limit(5);
      return (data ?? []) as unknown as Array<{
        id: string;
        student_name: string;
        days_since_last_session: number;
      }>;
    },
  });

  const draftsQuery = useQuery({
    queryKey: ["dashboard-draft-sessions", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("sessions")
        .select("id, session_date, coach_students(student_name)")
        .eq("coach_id", userId!)
        .eq("status", "draft")
        .is("discarded_at" as never, null)
        .order("session_date", { ascending: false })
        .limit(5);
      return (data ?? []).map((r: any) => ({
        id: r.id as string,
        date: r.session_date as string,
        name: (r.coach_students?.student_name as string) ?? "Client",
      }));
    },
  });

  const fetchForecasts = useServerFn(getForecastsForCoach);
  const fetchDefaultForecast = useServerFn(getDefaultLocationForecast);
  const forecastsQuery = useQuery({
    queryKey: ["dashboard-forecasts", userId],
    enabled: !!userId && !!session?.access_token,
    queryFn: async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const headers = currentSession?.access_token
        ? { Authorization: `Bearer ${currentSession.access_token}` }
        : undefined;
      if (!headers) return [];

      try {
        const forecasts = await fetchForecasts({ data: { days: 3 }, headers });
        return Array.isArray(forecasts) ? forecasts : [];
      } catch (error) {
        console.warn("Dashboard forecasts unavailable", error);
        return [];
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const defaultForecastQuery = useQuery({
    queryKey: ["dashboard-default-forecast", userId],
    enabled: !!userId && !!session?.access_token,
    queryFn: async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const headers = currentSession?.access_token
        ? { Authorization: `Bearer ${currentSession.access_token}` }
        : undefined;
      if (!headers) return { state: "fetch_failed" as const, locationName: null };
      try {
        return await fetchDefaultForecast({ headers });
      } catch (error) {
        console.warn("Default location forecast unavailable", error);
        return { state: "fetch_failed" as const, locationName: null };
      }
    },
    retry: false,
    staleTime: 10 * 60 * 1000,
  });


  const fetchCalendarEvents = useServerFn(getUpcomingCalendarEvents);
  const calendarEventsQuery = useQuery({
    queryKey: ["dashboard-calendar-events", userId],
    enabled: !!userId && !!session?.access_token,
    queryFn: async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const headers = currentSession?.access_token
        ? { Authorization: `Bearer ${currentSession.access_token}` }
        : undefined;
      if (!headers) return [] as UpcomingEvent[];
      try {
        const res = (await fetchCalendarEvents({ data: { days: 1 }, headers })) as
          | { connected: true; events: UpcomingEvent[] }
          | { connected: false };
        return res && "connected" in res && res.connected ? res.events : [];
      } catch (error) {
        console.warn("Dashboard calendar events unavailable", error);
        return [] as UpcomingEvent[];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const unbilled = useUnbilledSessions();

  const stats = statsQuery.data ?? { clients: 0, drills: 0, drafts: 0, sent: 0 };
  const recent = recentQuery.data ?? [];
  const todays = recent.filter((r) => r.date === todayISO);
  const doneToday = todays.filter((r) => r.status === "sent").length;
  const remainingToday = todays.length - doneToday;

  const allTodaysEvents = (calendarEventsQuery.data ?? [])
    .filter((e) => e.startDate === todayISO)
    .slice()
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  // Hide all-day events and Calendly-style dismissed/soloable ones from this glanceable list
  const todaysEvents = allTodaysEvents.filter((e) => e.start.length > 10);
  const todaysEventHours =
    todaysEvents.reduce((sum, e) => {
      const ms = new Date(e.end).getTime() - new Date(e.start).getTime();
      return sum + (Number.isFinite(ms) && ms > 0 ? ms : 60 * 60_000);
    }, 0) / 3_600_000;
  const nowMs = Date.now();
  const upNextEvent =
    todaysEvents.find((e) => new Date(e.end).getTime() >= nowMs) ?? todaysEvents[0] ?? null;
  const eventDisplayName = (e: UpcomingEvent) =>
    e.matchedClient?.name ?? e.attendee?.name ?? e.title ?? "Lesson";
  const eventDurationLabel = (e: UpcomingEvent) => {
    const mins = Math.round((new Date(e.end).getTime() - new Date(e.start).getTime()) / 60_000);
    return Number.isFinite(mins) && mins > 0 ? `${mins} min` : "60 min";
  };
  const eventTimeLabel = (e: UpcomingEvent) =>
    new Date(e.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  const upcomingForecasts = (Array.isArray(forecastsQuery.data) ? forecastsQuery.data : []).slice().sort(
    (a, b) => new Date(a.lesson_start_at).getTime() - new Date(b.lesson_start_at).getTime(),
  );
  // Prefer a lesson-attached forecast for today; otherwise the next upcoming
  // lesson forecast; otherwise fall back to the coach's default-location daily
  // forecast so the tile is never empty when a default venue is set.
  const defaultForecastData = defaultForecastQuery.data;
  const defaultForecastRow =
    defaultForecastData && "state" in defaultForecastData && defaultForecastData.state === "ok"
      ? defaultForecastData
      : null;
  const defaultForecastState =
    defaultForecastData && "state" in defaultForecastData ? defaultForecastData.state : null;
  const defaultForecastLocationName =
    defaultForecastData && "locationName" in defaultForecastData
      ? defaultForecastData.locationName
      : null;
  const todaysForecast =
    upcomingForecasts.find((f) => f.lesson_start_at.slice(0, 10) === todayISO) ??
    upcomingForecasts[0] ??
    defaultForecastRow ??
    null;


  // Status sentence
  const statusSentence = (() => {
    const parts: string[] = [];
    if (todays.length > 0) {
      parts.push(
        remainingToday === 0
          ? `All ${todays.length} done.`
          : `${doneToday} done, ${remainingToday} to go.`,
      );
    } else {
      parts.push("No lessons on the books today.");
    }
    if (todaysForecast?.temp_f != null) {
      const t = Math.round(todaysForecast.temp_f);
      const cond =
        todaysForecast.flag_reason ??
        (todaysForecast.precip_prob && todaysForecast.precip_prob > 30
          ? "light precip expected"
          : "court conditions look good");
      parts.push(`${cond} — ${t}°.`);
    } else {
      parts.push("Court conditions look clear.");
    }
    return parts.join(" ");
  })();

  const pendingNudges = nudgesQuery.data ?? [];
  const drafts = draftsQuery.data ?? [];

  // Money column
  const overdueCount = 0; // no true overdue flag wired here yet
  const unbilledTotal = unbilled.data?.estimatedTotalCents ?? 0;
  const unbilledSessions = unbilled.data?.totalSessions ?? 0;

  // Action hero: queue counts
  const recapsCount = stats.drafts;
  const reengageCount = pendingNudges.length;
  const queueTotal = recapsCount + reengageCount + (unbilledSessions > 0 ? 1 : 0);
  const queueSummary = [
    recapsCount > 0 ? `${recapsCount} lesson recap${recapsCount === 1 ? "" : "s"}` : null,
    reengageCount > 0 ? `${reengageCount} re-engage draft${reengageCount === 1 ? "" : "s"}` : null,
    unbilledSessions > 0 ? `${unbilledSessions} unbilled lesson${unbilledSessions === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" · ") || "Nothing waiting — you're caught up.";


  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {!onboarding.isLoading && !onboarding.isDone && (
          <Card className="mb-6 flex items-center gap-4 border-primary/30 bg-primary/5 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Rocket className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Finish setting up CoachQuick</p>
              <p className="text-xs text-muted-foreground">
                {[
                  onboarding.state?.step_profile_done,
                  onboarding.state?.step1_clients_done,
                  onboarding.state?.step4_calendar_done,
                  onboarding.state?.step3_log_done,
                ].filter(Boolean).length}
                {" of 4 steps complete — "}
                <Link to="/app/onboarding" className="font-medium text-brand-text hover:underline">
                  resume the tour
                </Link>
                .
              </p>
            </div>
            <Button variant="ghost" size="icon" aria-label="Dismiss" onClick={() => onboarding.skip()}>
              <X className="h-4 w-4" />
            </Button>
          </Card>
        )}

        {/* Greeting */}
        <section className="mb-6">
          <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground">{eyebrow}</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
            Good {partOfDay()}, {greetingName}.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{statusSentence}</p>
        </section>

        {/* Today's lessons */}
        <Card className="rounded-2xl border bg-card p-5 shadow-none">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-3">
              <h2 className="text-lg font-bold tracking-tight">Today's lessons</h2>
              <span className="text-xs text-muted-foreground">
                {todaysEvents.length} lesson{todaysEvents.length === 1 ? "" : "s"}
                {todaysEvents.length > 0 && ` · ${todaysEventHours.toFixed(1)} hrs`}
              </span>
            </div>
            <Link
              to="/app/calendar"
              className="flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
            >
              Full schedule <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {calendarEventsQuery.isLoading ? (
            <ul className="mt-4 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 py-2">
                  <Skeleton className="h-10 w-16" />
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                </li>
              ))}
            </ul>
          ) : todaysEvents.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              No lessons scheduled today.{" "}
              <Link to="/app/log" className="font-medium text-foreground hover:underline">
                Log one anyway →
              </Link>
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {todaysEvents.map((e) => {
                const isUpNext = upNextEvent?.id === e.id;
                const isDone = e.bucket === "logged";
                const name = eventDisplayName(e);
                return (
                  <li key={e.id} className="flex items-center gap-4 py-3">
                    <div className="w-20 shrink-0">
                      <p className="text-sm font-bold tracking-tight">{eventTimeLabel(e)}</p>
                      <p className="text-[11px] text-muted-foreground">{eventDurationLabel(e)}</p>
                    </div>
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary/20 text-xs font-semibold text-foreground">
                        {initials(name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold">{name}</p>
                      <p className="truncate text-xs text-muted-foreground">Lesson</p>
                    </div>
                    {isDone ? (
                      <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-medium text-success">
                        Done
                      </span>
                    ) : isUpNext ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-3 py-1 text-xs font-medium text-foreground">
                        <span className="relative inline-flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                        </span>
                        Up next
                      </span>
                    ) : e.sessionId ? (
                      <Link
                        to="/app/sessions/$sessionId"
                        params={{ sessionId: e.sessionId }}
                        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        Log <ArrowRight className="h-3 w-3" />
                      </Link>
                    ) : (
                      <Link
                        to="/app/log"
                        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        Log <ArrowRight className="h-3 w-3" />
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="mt-6">
          <DashboardAlerts />
        </div>

        {/* Action hero */}
        <div className="mt-6 rounded-2xl bg-[oklch(0.18_0.02_240)] p-6 text-background dark:bg-[oklch(0.14_0.01_240)]">
          <div className="grid gap-8 md:grid-cols-3">
            {/* SEND TODAY */}
            <div>
              <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">SEND TODAY</p>
              <p className="mt-2 font-display text-6xl font-black tracking-tight text-white">{queueTotal}</p>
              <p className="mt-3 text-sm text-white/70">{queueSummary}</p>
              {queueTotal > 0 ? (
                <Link to="/app/queue" className="mt-4 inline-block">
                  <Button className="rounded-[10px] bg-primary text-primary-foreground hover:bg-primary/90">
                    <Zap className="h-4 w-4" /> Run through queue
                  </Button>
                </Link>
              ) : (
                <p className="mt-4 inline-flex items-center gap-2 rounded-[10px] bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/70">
                  All clear
                </p>
              )}
            </div>

            {/* UP NEXT ON COURT */}
            <div>
              <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">UP NEXT ON COURT</p>
              {calendarEventsQuery.isLoading ? (
                <>
                  <Skeleton className="mt-2 h-9 w-32 bg-white/10" />
                  <Skeleton className="mt-2 h-4 w-40 bg-white/10" />
                </>
              ) : upNextEvent ? (
                <>
                  <p className="mt-2 font-display text-3xl font-black tracking-tight text-white">
                    {eventTimeLabel(upNextEvent)}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {eventDisplayName(upNextEvent)} · {eventDurationLabel(upNextEvent)}
                  </p>
                  <p className="mt-1 text-xs text-white/60">Lesson on the calendar</p>
                </>
              ) : (
                <p className="mt-2 text-sm text-white/60">Nothing else on the books today.</p>
              )}
            </div>

            {/* COURT FORECAST */}
            <div>
              <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">COURT FORECAST</p>
              {todaysForecast ? (
                <>
                  <div className="mt-2 flex items-center gap-3">
                    <WeatherIcon
                      weatherCode={todaysForecast.weather_code}
                      severity={todaysForecast.severity}
                      tempF={todaysForecast.temp_f}
                      precipProb={todaysForecast.precip_prob}
                      windMph={todaysForecast.wind_mph}
                      reason={todaysForecast.flag_reason}
                    />
                    <p className="font-display text-5xl font-black tracking-tight text-white">
                      {todaysForecast.temp_f != null ? `${Math.round(todaysForecast.temp_f)}°` : "--"}
                    </p>
                  </div>
                  <p className="mt-2 text-sm text-white/80">{todaysForecast.flag_reason ?? "Clear skies"}</p>
                  <p className="mt-1 text-xs text-white/60">
                    {todaysForecast.wind_mph != null && `Wind ${Math.round(todaysForecast.wind_mph)} mph`}
                    {todaysForecast.precip_prob != null && ` · ${Math.round(todaysForecast.precip_prob)}% precip`}
                  </p>
                  <Link
                    to="/app/weather"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-text hover:underline"
                  >
                    10-day outdoor view <ArrowRight className="h-3 w-3" />
                  </Link>
                </>
              ) : defaultForecastQuery.isPending || (defaultForecastQuery.isFetching && !defaultForecastData) ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-white/60">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking court conditions…
                </div>
              ) : defaultForecastState === "not_geocoded" ? (
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-white/80">
                    {defaultForecastLocationName ?? "Default location"} isn't geocoded yet.
                  </p>
                  <Link
                    to="/app/locations"
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-text hover:underline"
                  >
                    Re-save the venue <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              ) : defaultForecastState === "fetch_failed" ? (
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-white/80">
                    {defaultForecastLocationName ?? "Forecast"} unavailable right now.
                  </p>
                  <button
                    type="button"
                    onClick={() => defaultForecastQuery.refetch()}
                    disabled={defaultForecastQuery.isFetching}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-text hover:underline disabled:opacity-60 disabled:no-underline"
                  >
                    {defaultForecastQuery.isFetching ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" /> Retrying…
                      </>
                    ) : (
                      <>
                        Retry <ArrowRight className="h-3 w-3" />
                      </>
                    )}
                  </button>
                </div>
              ) : defaultForecastState === "no_default_location" ? (
                <p className="mt-2 text-sm text-white/60">
                  Add a default location to see forecasts.
                </p>
              ) : (
                <div className="mt-2 flex items-center gap-2 text-sm text-white/60">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking court conditions…
                </div>
              )}


            </div>
          </div>
        </div>

        {/* Three-column queue */}
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {/* Recaps */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-foreground" />
                <h3 className="text-sm font-bold tracking-tight">Recaps to send</h3>
              </div>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground">
                {recapsCount}
              </span>
            </div>
            {drafts.length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">All recaps sent.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {drafts.slice(0, 3).map((d) => (
                  <li key={d.id} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/20 text-[10px] font-semibold text-foreground">
                        {initials(d.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold">{d.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{d.date} · Automated draft ready</p>
                    </div>
                    <Link to="/app/sessions/$sessionId" params={{ sessionId: d.id }}>
                      <Button size="sm" className="h-7 rounded-[10px] px-3 text-xs">Review</Button>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Re-engage */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-foreground" />
                <h3 className="text-sm font-bold tracking-tight">Re-engage drafts</h3>
              </div>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground">
                {reengageCount}
              </span>
            </div>
            {pendingNudges.length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">All caught up.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {pendingNudges.slice(0, 3).map((n) => (
                  <li key={n.id} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/20 text-[10px] font-semibold text-foreground">
                        {initials(n.student_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-semibold">{n.student_name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {n.days_since_last_session} days since last
                      </p>
                    </div>
                    <Link to="/app/reengagement">
                      <Button size="sm" className="h-7 rounded-[10px] px-3 text-xs">Review</Button>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Money */}
          <Card className="rounded-2xl border bg-card p-5 shadow-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-foreground" />
                <h3 className="text-sm font-bold tracking-tight">Money</h3>
              </div>
              {overdueCount > 0 && (
                <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold text-destructive">
                  {overdueCount} overdue
                </span>
              )}
            </div>
            {unbilledSessions === 0 && overdueCount === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">Nothing outstanding.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {unbilledSessions > 0 && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {unbilledSessions} unbilled lesson{unbilledSessions === 1 ? "" : "s"}
                      {unbilledTotal > 0 ? ` · ${fmtMoney(unbilledTotal)}` : ""}
                    </p>
                    <ul className="space-y-3">
                      {(unbilled.data?.groups ?? []).slice(0, 3).map((g) => {
                        const dates = g.sessions
                          .slice(-2)
                          .reverse()
                          .map((s) =>
                            new Date(s.session_date).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            }),
                          );
                        const extra = g.sessions.length - dates.length;
                        return (
                          <li key={g.clientId} className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-primary/20 text-[10px] font-semibold text-foreground">
                                {initials(g.clientName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-sm font-semibold">{g.clientName}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {g.sessions.length} lesson{g.sessions.length === 1 ? "" : "s"}
                                {dates.length > 0 ? ` · ${dates.join(", ")}` : ""}
                                {extra > 0 ? ` +${extra}` : ""}
                                {g.estimatedTotalCents > 0
                                  ? ` · ${fmtMoney(g.estimatedTotalCents)}`
                                  : ""}
                              </p>
                            </div>
                            <Link to="/app/invoices">
                              <Button
                                size="sm"
                                className="h-7 rounded-[10px] px-3 text-xs"
                              >
                                Review
                              </Button>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                    {(unbilled.data?.groups.length ?? 0) > 3 && (
                      <p className="text-xs text-muted-foreground">
                        +{(unbilled.data?.groups.length ?? 0) - 3} more client
                        {(unbilled.data?.groups.length ?? 0) - 3 === 1 ? "" : "s"}
                      </p>
                    )}
                    <Link to="/app/invoices" className="inline-block">
                      <Button size="sm" variant="secondary" className="h-7 rounded-[10px] px-3 text-xs">
                        Create invoices
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            )}
          </Card>

        </div>
      </div>
    </AppShell>
  );
}
